import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import api from "src/utils/api";
import { fetchHostBookings, money } from "src/utils/bookings";

// Gate check-in for events: scan the QR on a guest's ticket (where the
// browser supports camera barcode scanning) or type the short code printed
// under it. The API admits each ticket exactly once.

const RESULT_STYLE = {
  admitted: "ok",
  already_used: "warn",
  expired: "warn",
  wrong_event: "warn",
  not_found: "bad",
  not_your_event: "bad",
};

const RESULT_TITLE = {
  admitted: "Admitted",
  already_used: "Already used",
  expired: "Event has ended",
  wrong_event: "Wrong event",
  not_found: "Ticket not found",
  not_your_event: "Not your event",
};

const canScan = typeof window !== "undefined" && "BarcodeDetector" in window;

const TicketCheckIn = () => {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [code, setCode] = useState("");
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [sales, setSales] = useState([]);
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    api
      .get("events/host")
      .then((res) => setEvents(res.data.data || []))
      .catch(() => {});
    fetchHostBookings().then(({ items }) => setSales(items.filter((b) => b.kind === "event")));
    return () => stopScanning();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tickets sold per event, from the host's event bookings.
  const salesByEvent = useMemo(() => {
    const groups = {};
    sales.forEach((b) => {
      const g = groups[b.listing] || { name: b.listing, tickets: 0, bookings: 0, revenue: {} };
      g.tickets += Number(b.guests) || 0;
      g.bookings += 1;
      g.revenue[b.currency || "UGX"] = (g.revenue[b.currency || "UGX"] || 0) + (Number(b.amount) || 0);
      groups[b.listing] = g;
    });
    return Object.values(groups);
  }, [sales]);

  const verify = async (value) => {
    const ticketCode = String(value || code).trim();
    if (!ticketCode) return;
    setChecking(true);
    try {
      const res = await api.post("events/tickets/verify", { code: ticketCode, event_id: eventId || undefined });
      setResult(res.data);
    } catch (error) {
      setResult(
        error?.response?.data || { result: "not_found", message: "Couldn't check this ticket. Please try again." },
      );
    } finally {
      setChecking(false);
      setCode("");
    }
  };

  const stopScanning = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setScanning(false);
  };

  const startScanning = async () => {
    setResult(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      setScanning(true);
      const video = videoRef.current;
      video.srcObject = stream;
      await video.play();
      const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
      const tick = async () => {
        if (!streamRef.current) return;
        try {
          const codes = await detector.detect(video);
          if (codes.length) {
            stopScanning();
            verify(codes[0].rawValue);
            return;
          }
        } catch (e) {
          // keep scanning
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    } catch (e) {
      stopScanning();
      setResult({ result: "not_found", message: "Camera unavailable. Type the code from the ticket instead." });
    }
  };

  return (
    <>
      <Helmet>
        <title>Ticket check-in | Tubayo Host</title>
      </Helmet>
      <div className="hd-page">
        <div className="hd-page-header">
          <div>
            <h1>Ticket check-in</h1>
            <p>Scan guests' tickets at the entrance. Each ticket can only be admitted once.</p>
          </div>
        </div>

        <div className="hd-checkin">
          <section className="hd-card hd-checkin-panel">
            <h3>Check a ticket</h3>
            <label className="hd-muted" htmlFor="checkin-event" style={{ fontSize: 13 }}>
              Event (optional — warns about tickets for other events)
            </label>
            <select
              id="checkin-event"
              className="hd-input"
              style={{ margin: "6px 0 14px" }}
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
            >
              <option value="">Any of my events</option>
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.event_name}
                </option>
              ))}
            </select>

            {canScan && (
              <>
                <div className="hd-scanner" style={{ display: scanning ? "block" : "none" }}>
                  <video ref={videoRef} muted playsInline />
                </div>
                <button
                  type="button"
                  className="hd-btn hd-btn-block"
                  style={{ marginBottom: 12 }}
                  onClick={scanning ? stopScanning : startScanning}
                >
                  <i className="fas fa-camera" /> {scanning ? "Stop camera" : "Scan QR code"}
                </button>
              </>
            )}

            <form
              style={{ display: "flex", gap: 8 }}
              onSubmit={(e) => {
                e.preventDefault();
                verify();
              }}
            >
              <input
                className="hd-input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Ticket code, e.g. 013A4"
                aria-label="Ticket code"
                autoComplete="off"
              />
              <button type="submit" className="hd-btn hd-btn-primary" disabled={checking || !code.trim()}>
                {checking ? "Checking…" : "Check"}
              </button>
            </form>

            {result && (
              <div className={`hd-result ${RESULT_STYLE[result.result] || "bad"}`} role="status">
                <strong>{RESULT_TITLE[result.result] || "Couldn't check ticket"}</strong>
                {result.message}
                {result.ticket && (
                  <div style={{ marginTop: 8 }}>
                    {result.ticket.holder} · {result.ticket.admits} × {result.ticket.ticket} · {result.ticket.event}
                    {result.ticket.checked_in_at && result.result !== "admitted" && (
                      <div>Checked in {new Date(result.ticket.checked_in_at).toLocaleString()}</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </section>

          <section className="hd-card hd-checkin-panel">
            <h3>Tickets sold</h3>
            {salesByEvent.length === 0 ? (
              <p className="hd-muted">No ticket sales yet.</p>
            ) : (
              salesByEvent.map((g) => (
                <div key={g.name} className="hd-event-row">
                  <div className="hd-cell-title">
                    <strong>{g.name}</strong>
                    <span>
                      {g.bookings} booking{g.bookings === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <strong>
                      {g.tickets} ticket{g.tickets === 1 ? "" : "s"}
                    </strong>
                    <div className="hd-muted" style={{ fontSize: 13 }}>
                      {Object.entries(g.revenue)
                        .map(([currency, total]) => money(total, currency))
                        .join(" + ")}
                    </div>
                  </div>
                </div>
              ))
            )}
          </section>
        </div>
      </div>
    </>
  );
};

export default TicketCheckIn;
